import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { PlusIcon, XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CountryMap,
  CountryMapKind,
  entryError,
  removeCountryEntry,
  setCountryEntry,
  sortedEntries,
} from "@/pages/movies/lib/country-map";

/**
 * Editor for a per-country map (`certifications` or `releaseDates`). The whole
 * map is saved, because the backend replaces the stored map with what it gets.
 */
function CountryMapField({
  id,
  kind,
}: AutoFormFieldProps & { kind: CountryMapKind }) {
  const form = useFormContext();
  const value: CountryMap | null = useWatch({ name: id }) ?? null;
  const [code, setCode] = useState("");
  const [text, setText] = useState("");
  const [error, setError] = useState<string>();
  const inputType = kind === "date" ? "date" : "text";

  const write = (next: CountryMap) =>
    form.setValue(id, next, { shouldDirty: true });

  const add = () => {
    const problem = entryError(kind, code, text);
    setError(problem);
    if (problem) return;
    write(setCountryEntry(value, code, text));
    setCode("");
    setText("");
  };

  return (
    <div className="flex w-full flex-col space-y-2 text-foreground">
      {sortedEntries(value).map(([country, entry]) => (
        <div key={country} className="flex flex-row items-center gap-2">
          <span className="w-10 font-mono text-sm">{country}</span>
          <Input
            aria-label={`${country} value`}
            className="w-[220px]"
            type={inputType}
            defaultValue={entry}
            onBlur={(e) => {
              const next = e.target.value;
              if (next === entry) return;
              if (entryError(kind, country, next)) {
                e.target.value = entry;
                return;
              }
              write(setCountryEntry(value, country, next));
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove ${country}`}
            onClick={() => write(removeCountryEntry(value, country))}
          >
            <XIcon className="size-4" />
          </Button>
        </div>
      ))}
      <div className="flex flex-row items-center gap-2">
        <Input
          aria-label="Country code"
          className="w-16 font-mono uppercase"
          maxLength={2}
          placeholder="GR"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Input
          aria-label="New value"
          className="w-[220px]"
          type={inputType}
          placeholder={kind === "date" ? "yyyy-MM-dd" : "e.g. Κ-12"}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button type="button" variant="outline" size="sm" onClick={add}>
          <PlusIcon className="size-4" /> Add
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export function CertificationsField(props: AutoFormFieldProps) {
  return <CountryMapField {...props} kind="certification" />;
}

export function ReleaseDatesField(props: AutoFormFieldProps) {
  return <CountryMapField {...props} kind="date" />;
}
